/* eslint-disable max-lines */
import {
  UnifiedAlarm,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { setDataVersionParam } from '@hakimo-ui/hakimo/util';
import { useMemo, useRef, useState } from 'react';
import { alarmColor } from '../../utils/alarmColor';
import {
  useAlarmGroupCamera,
  useAlarmGroupPlayback,
  useAlarmGroupStatic,
  useAlarmGroupTime,
} from '../alarm-provider/AlarmGroupProvider';
import { COMPRESSED_TIMELINE_HEIGHT } from '../constants';
import DetectionMarker from './DetectionMarker';
import StatsPreview, { SegmentStats } from './StatsPreview';
import { useFaceMarkers } from './useFaceMarkers';
import { useWeaponMarkers } from './useWeaponMarkers';
import { bridgeGaps, getDominantAlarm } from './utils';
dayjs.extend(utc);

interface CompressedSegment {
  startTime: number;
  endTime: number;
  alarms: UnifiedAlarm[];
  dominantAlarm: UnifiedAlarm; // The alarm that determines the color/priority
  stats: SegmentStats; // Pre-calculated statistics
}

interface Props {
  theme?: 'system' | 'dark';
  viewStart?: number;
  viewEnd?: number;
}

export function CompressedTimeline(props: Props) {
  const { theme = 'dark', viewStart, viewEnd } = props;

  const {
    location,
    alarmCameraIds,
    product,
    groupHasFrames,
    isScanLike,
    dataVersion,
  } = useAlarmGroupStatic();
  const { seekTo } = useAlarmGroupTime();
  const {
    timelineBounds: { totalTimelineStartTime, totalTimelineEndTime },
  } = useAlarmGroupPlayback();
  const { camToDetailsMap } = useAlarmGroupCamera();

  // Use zoom view bounds when provided, otherwise fall back to full timeline bounds
  const effectiveStart = viewStart ?? totalTimelineStartTime;
  const effectiveEnd = viewEnd ?? totalTimelineEndTime;

  const timelineRef = useRef<HTMLDivElement>(null);
  // State for preview tooltip (when hovering on a segment). Stored as viewport
  // pixel coordinates so the portal'd preview is not clipped by the timeline's
  // overflow-x-clip ancestor.
  const [hoverPreviewCoords, setHoverPreviewCoords] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [hoverSegment, setHoverSegment] = useState<CompressedSegment | null>(
    null
  );

  const timelineDuration = useMemo(() => {
    return effectiveEnd - effectiveStart;
  }, [effectiveEnd, effectiveStart]);

  // Get all alarms from all cameras
  const allAlarms = useMemo(() => {
    const alarms: UnifiedAlarm[] = [];
    alarmCameraIds.forEach((cameraId) => {
      const cameraDetails = camToDetailsMap[cameraId];
      if (cameraDetails?.alarms) {
        alarms.push(...cameraDetails.alarms);
      }
    });
    return alarms.sort((a, b) => a.alarmStartTime - b.alarmStartTime);
  }, [alarmCameraIds, camToDetailsMap]);

  const weaponMarkers = useWeaponMarkers(allAlarms);
  const faceMarkers = useFaceMarkers(allAlarms);

  // Compress overlapping alarms into unified segments
  const compressedSegments = useMemo(() => {
    if (allAlarms.length === 0) return [];

    // Collect all unique time points (start and end times)
    const timePoints = new Set<number>();
    allAlarms.forEach((alarm) => {
      timePoints.add(alarm.alarmStartTime);
      timePoints.add(alarm.alarmEndTime);
    });

    const sortedTimePoints = Array.from(timePoints).sort((a, b) => a - b);
    const segments: CompressedSegment[] = [];

    // Process each time segment between consecutive time points
    for (let i = 0; i < sortedTimePoints.length - 1; i++) {
      const segmentStart = sortedTimePoints[i];
      const segmentEnd = sortedTimePoints[i + 1];

      // Find all active alarms in this time segment
      const activeAlarms = allAlarms.filter(
        (alarm) =>
          alarm.alarmStartTime <= segmentStart &&
          alarm.alarmEndTime >= segmentEnd
      );

      if (activeAlarms.length === 0) {
        continue; // No active alarms in this segment
      }

      // Determine dominant alarm (highest priority)
      const dominantAlarm = getDominantAlarm(activeAlarms);

      // Calculate stats for this segment
      const calculateStats = (
        alarms: UnifiedAlarm[],
        start: number,
        end: number
      ): SegmentStats => {
        let criticalCount = 0;
        let minorCount = 0;
        // TODO: remove this once we have a proper way to filter out minor alarms
        //
        // `isScanLike`, not `groupHasFrames`: it is `product === SCAN` on v2 and
        // so correct from the first render, before media arrives. Gap bridging
        // below does use `groupHasFrames` -- that is about frames, not scan.
        for (const alarm of alarms) {
          const isMinor = isScanLike
            ? alarm.severity === 'low'
            : alarm.resolvedAlarm;
          if (isMinor) {
            minorCount++;
          } else {
            criticalCount++;
          }
        }
        return {
          criticalCount,
          minorCount,
          startTime: start,
          endTime: end,
        };
      };

      // Merge with previous segment if same dominant alarm and consecutive
      const lastSegment = segments[segments.length - 1];
      if (
        lastSegment &&
        lastSegment.dominantAlarm.id === dominantAlarm.id &&
        lastSegment.endTime === segmentStart
      ) {
        lastSegment.endTime = segmentEnd;
        // Update alarms list to include all unique alarms
        const uniqueAlarms = new Set([...lastSegment.alarms, ...activeAlarms]);
        lastSegment.alarms = Array.from(uniqueAlarms);
        // Recalculate stats for merged segment
        lastSegment.stats = calculateStats(
          lastSegment.alarms,
          lastSegment.startTime,
          lastSegment.endTime
        );
      } else {
        segments.push({
          startTime: segmentStart,
          endTime: segmentEnd,
          alarms: activeAlarms,
          dominantAlarm,
          stats: calculateStats(activeAlarms, segmentStart, segmentEnd),
        });
      }
    }

    return segments;
  }, [allAlarms, isScanLike]);

  // Bridge held-frame gaps so the compressed view matches the player and the
  // per-camera timeline (see bridgeGaps). Segments are already sorted; endTime is
  // untouched so stats/hover stay exact — only the rendered bar width bridges.
  const segmentBarEnds = useMemo(() => {
    if (!groupHasFrames) {
      return compressedSegments.map((s) => s.endTime);
    }
    return bridgeGaps(
      compressedSegments,
      (s) => s.startTime,
      (s) => s.endTime
    );
  }, [compressedSegments, groupHasFrames]);

  const onHandleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.metaKey || e.ctrlKey) {
      return;
    }
    const el = timelineRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const clickX = e.clientX - rect.left;

    const clampedX = Math.max(0, Math.min(clickX, rect.width));
    const fraction = clampedX / rect.width;

    const clickedTime =
      effectiveStart + fraction * (effectiveEnd - effectiveStart);

    seekTo(clickedTime);
  };

  // Calculate left position (%) for a segment
  const getSegmentPosition = (segment: CompressedSegment) => {
    const diff = segment.startTime - effectiveStart;
    return (diff / timelineDuration) * 100;
  };

  // Handler for hovering over a segment.
  const onHoverSegment = (segment: CompressedSegment) => () => {
    if (timelineRef.current) {
      const rect = timelineRef.current.getBoundingClientRect();
      const segmentMidPoint =
        segment.startTime + (segment.endTime - segment.startTime) / 2;
      const diff = segmentMidPoint - effectiveStart;
      const fraction = diff / timelineDuration;
      setHoverPreviewCoords({
        x: rect.left + fraction * rect.width,
        y: rect.top,
      });
      setHoverSegment(segment);
    }
  };

  const onMouseLeaveSegment = () => {
    setHoverPreviewCoords(null);
    setHoverSegment(null);
  };

  const onSegmentClick =
    (segment: CompressedSegment) => (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.metaKey || e.ctrlKey) {
        const alarm = segment.dominantAlarm;
        const searchParams = new URLSearchParams();
        if (product === UnifiedMonitoringProduct.SCAN) {
          searchParams.set('product', 'scan');
        } else {
          const date = dayjs.utc(alarm.alarmStartTime);
          const partitionKey = date.format('YYYYMM');
          searchParams.set('partitionKey', partitionKey);
          setDataVersionParam(searchParams, dataVersion);
        }
        const url = `/alarms/${alarm.id}?${searchParams.toString()}`;
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
    };

  return (
    <div
      ref={timelineRef}
      className={clsx(
        'relative cursor-pointer',
        theme === 'system'
          ? 'bg-onlight-bg-3 dark:bg-ondark-bg-3/80'
          : 'bg-ondark-bg-1/80'
      )}
      style={{ height: `${COMPRESSED_TIMELINE_HEIGHT}px` }}
      onClick={onHandleTimelineClick}
    >
      <div className="absolute inset-0 overflow-hidden">
        {compressedSegments.map((segment, index) => {
          const left = getSegmentPosition(segment);
          const barEnd = segmentBarEnds[index] ?? segment.endTime;
          const width = ((barEnd - segment.startTime) / timelineDuration) * 100;

          return (
            <div
              key={`${segment.startTime}-${index}`}
              onMouseOverCapture={onHoverSegment(segment)}
              onMouseLeave={onMouseLeaveSegment}
              className={clsx(
                'absolute top-0 h-full transition-transform',
                alarmColor(segment.dominantAlarm)
              )}
              style={{ left: `${left}%`, width: `${width}%` }}
              tabIndex={1}
              onClick={onSegmentClick(segment)}
            ></div>
          );
        })}
        {weaponMarkers.map((marker) => {
          const markerLeft =
            ((marker.absoluteTime - effectiveStart) / timelineDuration) * 100;
          return (
            <DetectionMarker
              key={`weapon-${marker.alarmId}-${marker.frame}`}
              leftPercent={markerLeft}
              height={COMPRESSED_TIMELINE_HEIGHT}
              label="Weapon detected"
              onClick={() => seekTo(marker.absoluteTime)}
            />
          );
        })}
        {faceMarkers.map((marker) => {
          const markerLeft =
            ((marker.absoluteTime - effectiveStart) / timelineDuration) * 100;
          return (
            <DetectionMarker
              key={`face-${marker.alarmId}-${marker.profileId}-${marker.absoluteTime}`}
              leftPercent={markerLeft}
              height={COMPRESSED_TIMELINE_HEIGHT}
              label={marker.label}
              onClick={() => seekTo(marker.absoluteTime)}
            />
          );
        })}
      </div>
      {hoverPreviewCoords !== null && hoverSegment && (
        <StatsPreview
          x={hoverPreviewCoords.x}
          y={hoverPreviewCoords.y}
          stats={hoverSegment.stats}
          timezone={location?.timezone ?? 'UTC'}
        />
      )}
    </div>
  );
}

export default CompressedTimeline;
